/**
 * Gift Card Quantity Blocker
 * Previene l'aggiornamento della quantità per le Gift Card nel carrello
 */
class GiftCardQuantityBlocker {
  constructor() {
    this.cartCache = null;
    this.cartCacheTime = 0;
    this.CACHE_TTL = 2000; // Cache valida per 2 secondi
    this.debounceTimer = null;
    this.isProcessing = false;
    // Salva riferimento originale a fetch PRIMA di intercettarlo
    this.originalFetch = window.fetch.bind(window);
    this.init();
  }

  init() {
    // Intercetta le richieste di aggiornamento carrello
    this.interceptCartUpdate();

    // Blocca gli input di quantità per le Gift Card
    this.blockQuantityInputs();

    // Osserva cambiamenti nel DOM per bloccare nuovi input
    this.observeCartChanges();
  }

  interceptCartUpdate() {
    // Intercetta fetch per /cart/update.js e /cart/change.js
    const self = this;

    window.fetch = async function(url, options) {
      if (typeof url === 'string' && (url.includes('/cart/update') || url.includes('/cart/change'))) {
        // Verifica se si sta tentando di aggiornare una Gift Card
        if (options && options.body) {
          let bodyData;

          try {
            if (typeof options.body === 'string') {
              bodyData = JSON.parse(options.body);
            } else if (options.body instanceof FormData) {
              bodyData = Object.fromEntries(options.body);
            }

            if (bodyData) {
              // Controlla se si sta tentando di aggiornare la quantità di una Gift Card
              const cart = await self.getCart();
              if (cart && cart.items) {
                let modified = false;

                // Controlla ogni update
                if (bodyData.updates) {
                  Object.keys(bodyData.updates).forEach(key => {
                    const item = cart.items.find(i => i.key === key);
                    if (item && self.isGiftCard(item)) {
                      // Forza la quantità a 1 o 0 (rimozione)
                      if (bodyData.updates[key] > 1) {
                        console.warn('[GiftCardQuantityBlocker] Bloccato tentativo di aggiornare quantità Gift Card');
                        bodyData.updates[key] = 1;
                        modified = true;
                        self.showNotification();
                      }
                    }
                  });

                  if (modified) {
                    options.body = JSON.stringify(bodyData);
                  }
                }

                // Per richieste di tipo change (singolo item)
                if (bodyData.quantity && bodyData.id) {
                  const item = cart.items.find(i => i.variant_id == bodyData.id || i.key === bodyData.id);
                  if (item && self.isGiftCard(item)) {
                    if (bodyData.quantity > 1) {
                      console.warn('[GiftCardQuantityBlocker] Bloccato tentativo di aggiornare quantità Gift Card');
                      bodyData.quantity = 1;
                      options.body = JSON.stringify(bodyData);
                      self.showNotification();
                    }
                  }
                }
              }
            }
          } catch (error) {
            console.error('[GiftCardQuantityBlocker] Errore nel parsing della richiesta:', error);
          }
        }

        // Invalida la cache dopo un update del carrello
        self.invalidateCache();
      }

      return self.originalFetch(url, options);
    };
  }

  invalidateCache() {
    this.cartCache = null;
    this.cartCacheTime = 0;
  }

  async getCart(forceRefresh = false) {
    const now = Date.now();

    // Usa la cache se valida e non forzato il refresh
    if (!forceRefresh && this.cartCache && (now - this.cartCacheTime) < this.CACHE_TTL) {
      return this.cartCache;
    }

    try {
      // Usa originalFetch per evitare ricorsione con l'intercettore
      const response = await this.originalFetch('/cart.js');
      this.cartCache = await response.json();
      this.cartCacheTime = now;
      return this.cartCache;
    } catch (error) {
      console.error('[GiftCardQuantityBlocker] Errore nel recupero del carrello:', error);
      return null;
    }
  }

  isGiftCard(item) {
    return item.product_type === 'Gift Card' ||
           item.properties?.['Gift card recipient'] === 'yes' ||
           (item.product_type && item.product_type.toLowerCase().includes('gift'));
  }

  async blockQuantityInputs() {
    // Evita esecuzioni parallele
    if (this.isProcessing) return;
    this.isProcessing = true;

    try {
      // Recupera il cart UNA SOLA VOLTA
      const cart = await this.getCart();
      if (!cart || !cart.items) {
        this.isProcessing = false;
        return;
      }

      // Blocca tutti gli input di quantità per le Gift Card
      const elements = document.querySelectorAll('[data-key]');
      elements.forEach(element => {
        this.checkAndBlockElement(element, cart);
      });
    } finally {
      this.isProcessing = false;
    }
  }

  checkAndBlockElement(element, cart) {
    const key = element.dataset.key;
    if (!key || !cart || !cart.items) return;

    const item = cart.items.find(i => i.key === key);
    if (item && this.isGiftCard(item)) {
      // Trova e blocca l'input di quantità
      const input = element.querySelector('input[type="number"], input.quantity');
      if (input && !input.classList.contains('gift-card-quantity-disabled')) {
        input.max = '1';
        input.min = '1';
        input.readOnly = true;
        input.disabled = true;
        input.title = 'Le Gift Card sono limitate a 1 per ordine';

        // Aggiungi classe CSS per styling
        input.classList.add('gift-card-quantity-disabled');
      }

      // Disabilita i pulsanti + e -
      const plusBtn = element.querySelector('[data-action="plus"], .plus, button[aria-label*="aumenta"]');
      const minusBtn = element.querySelector('[data-action="minus"], .minus, button[aria-label*="diminuisci"]');

      if (plusBtn && !plusBtn.disabled) {
        plusBtn.disabled = true;
        plusBtn.style.opacity = '0.5';
        plusBtn.style.cursor = 'not-allowed';
      }

      if (minusBtn && !minusBtn.disabled) {
        minusBtn.disabled = true;
        minusBtn.style.opacity = '0.5';
        minusBtn.style.cursor = 'not-allowed';
      }
    }
  }

  observeCartChanges() {
    const observer = new MutationObserver(() => {
      // Debounce per evitare chiamate multiple
      if (this.debounceTimer) {
        clearTimeout(this.debounceTimer);
      }

      this.debounceTimer = setTimeout(() => {
        // Invalida la cache prima di ri-bloccare
        this.invalidateCache();
        this.blockQuantityInputs();
      }, 300); // 300ms di debounce
    });

    // Osserva il carrello e il drawer
    const cartContainers = document.querySelectorAll('.main-cart, #cart-drawer, [data-cart-items], .cart-items');
    cartContainers.forEach(container => {
      if (container) {
        observer.observe(container, {
          childList: true,
          subtree: false // Solo figli diretti per ridurre i trigger
        });
      }
    });
  }

  showNotification() {
    // Rimuovi notifiche esistenti
    document.querySelectorAll('.gift-card-quantity-warning').forEach(n => n.remove());
    
    const notification = document.createElement('div');
    notification.className = 'gift-card-quantity-warning alert alert-warning position-fixed start-50 translate-middle-x shadow';
    notification.style.cssText = 'z-index: 9999; top: 100px; min-width: 300px;';
    notification.innerHTML = `
      <div class="d-flex align-items-center gap-2">
        <svg width="20" height="20" fill="currentColor" viewBox="0 0 16 16">
          <path d="M8.982 1.566a1.13 1.13 0 0 0-1.96 0L.165 13.233c-.457.778.091 1.767.98 1.767h13.713c.889 0 1.438-.99.98-1.767L8.982 1.566zM8 5c.535 0 .954.462.9.995l-.35 3.507a.552.552 0 0 1-1.1 0L7.1 5.995A.905.905 0 0 1 8 5zm.002 6a1 1 0 1 1 0 2 1 1 0 0 1 0-2z"/>
        </svg>
        <div>
          <strong>Limite quantità Gift Card</strong><br>
          <small>Le Gift Card possono essere acquistate solo in quantità singola (1 per ordine)</small>
        </div>
        <button type="button" class="btn-close" aria-label="Chiudi"></button>
      </div>
    `;
    
    document.body.appendChild(notification);
    
    // Chiudi al click
    notification.querySelector('.btn-close').addEventListener('click', () => notification.remove());
    
    // Auto-rimuovi dopo 5 secondi
    setTimeout(() => notification.remove(), 5000);
  }
}

// Inizializza quando il DOM è pronto
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    window.giftCardQuantityBlocker = new GiftCardQuantityBlocker();
  });
} else {
  window.giftCardQuantityBlocker = new GiftCardQuantityBlocker();
}